// Time-of-run lighting curve — a deterministic day → sunset → dusk arc driven purely by
// run time, so the world visibly escalates as a run goes deep (the survivor "it's getting
// late and dangerous" feel). Pure + headless-testable: the renderer reads these factors
// each frame and lerps the per-biome base lighting/fog toward them. No render/sim deps.

export interface DayLight {
  /** Eased run progress, 0 (run start, full day) .. 1 (deep run, dusk). */
  p: number
  /** Multiplier on light intensity — 1 at start, floors at 0.6 so the swarm stays readable. */
  intensity: number
  /** 0..1 blend toward the warm sunset tint; a bump that peaks mid-run and fades by dusk. */
  warm: number
  /** 0..1 blend toward the cool dusk tint; rises through the back of the run. */
  cool: number
  /** Multiplier pulling fog near/far inward — 1 at start, 0.75 late so the horizon closes in. */
  fogScale: number
}

/** Seconds for a full day → dusk arc — a standard 10:00 survive run. */
export const DAY_LENGTH = 600

/**
 * Map run time to lighting factors. Smoothstep-eased so the shift is gentle early and
 * settles late; the warm bump and cool rise overlap to read as day → sunset → dusk.
 * Clamped, so over-time runs simply hold at dusk.
 */
export function dayLight(time: number, dayLength: number = DAY_LENGTH): DayLight {
  const raw = dayLength > 0 ? time / dayLength : 0
  const p = Math.max(0, Math.min(1, raw))
  const e = p * p * (3 - 2 * p) // smoothstep
  // warm sunset bump: 0 at start, peaks ~p=0.62, gone by dusk
  const warm = Math.max(0, Math.min(1, 1 - ((p - 0.62) / 0.34) ** 2))
  // cool dusk: nothing until past mid-run, then ramps to full by the end
  const cool = Math.max(0, Math.min(1, (p - 0.55) / 0.45))
  const intensity = 1 - e * 0.4 // 1 → 0.6
  const fogScale = 1 - e * 0.25 // 1 → 0.75
  return { p: e, intensity, warm, cool, fogScale }
}

/**
 * RGB multipliers for an ordinary swarm enemy as the run reaches dusk (cool = dayLight().cool):
 * a subtle hotter/redder, slightly darker "menace" tint so the late-run swarm reads more
 * threatening. Identity {1,1,1} at day → render is byte-identical early/mid-run. Pure + clamped.
 */
export function enemyDuskTint(cool: number): { r: number; g: number; b: number } {
  const c = Math.max(0, Math.min(1, cool))
  return { r: 1 + c * 0.4, g: 1 - c * 0.08, b: 1 - c * 0.22 }
}
